// ============================================================
// ORGANIZATION HOOK
// File: src/hooks/useOrganization.js
// ============================================================
// Hook to access current user's organization and settings
// Supports super_admin role with organization selection

import { useState, useEffect } from 'react'
import { useAuth } from '../AuthContext'
import { supabase } from '../supabaseClient'

const SELECTED_ORG_KEY = 'selected_organization_id'
const ORG_CACHE_KEY = 'cached_organization_data'
const ROLE_CACHE_KEY = 'cached_user_role'
const USER_ID_CACHE_KEY = 'cached_user_id'

export function useOrganization() {
  const { user } = useAuth()

  // Restore from cache for instant display
  const [organization, setOrganization] = useState(() => {
    try {
      const cached = localStorage.getItem(ORG_CACHE_KEY)
      return cached ? JSON.parse(cached) : null
    } catch {
      return null
    }
  })

  const [userRole, setUserRole] = useState(() => {
    try {
      return localStorage.getItem(ROLE_CACHE_KEY)
    } catch {
      return null
    }
  })

  const [userId, setUserId] = useState(() => {
    try {
      return localStorage.getItem(USER_ID_CACHE_KEY)
    } catch {
      return null
    }
  })

  const [loading, setLoading] = useState(!organization) // Only show loading if no cached data
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchOrganization() {
      if (!user) {
        setOrganization(null)
        setUserRole(null)
        setUserId(null)
        setLoading(false)
        return
      }

      try {
        // Only set loading if we don't have cached data
        if (!organization) {
          setLoading(true)
        }
        setError(null)

        // Get user with organization data and role
        // Query by email since user.id from Supabase Auth may not match users table id
        const { data, error: fetchError } = await supabase
          .from('users')
          .select(`
            id,
            organization_id,
            role,
            organizations (
              id,
              name,
              slug,
              settings,
              features,
              active
            )
          `)
          .eq('email', user.email)
          .single()

        if (fetchError) throw fetchError

        // Cache role and user id
        setUserRole(data?.role)
        setUserId(data?.id || null)
        try {
          localStorage.setItem(ROLE_CACHE_KEY, data?.role || '')
          if (data?.id) localStorage.setItem(USER_ID_CACHE_KEY, data.id)
        } catch (e) {
          console.error('Error caching user data:', e)
        }

        // If super_admin, check for selected organization
        if (data?.role === 'super_admin') {
          const selectedOrgId = localStorage.getItem(SELECTED_ORG_KEY)

          if (selectedOrgId) {
            // Fetch the selected organization
            const { data: orgData, error: orgError } = await supabase
              .from('organizations')
              .select('*')
              .eq('id', selectedOrgId)
              .single()

            if (orgError) {
              console.error('Error fetching selected organization:', orgError)
              // Clear invalid selection
              localStorage.removeItem(SELECTED_ORG_KEY)
              localStorage.removeItem(ORG_CACHE_KEY)
              setOrganization(null)
            } else {
              setOrganization(orgData)
              // Cache organization
              try {
                localStorage.setItem(ORG_CACHE_KEY, JSON.stringify(orgData))
              } catch (e) {
                console.error('Error caching organization:', e)
              }
            }
          } else {
            // Super admin hasn't selected an org yet
            setOrganization(null)
            localStorage.removeItem(ORG_CACHE_KEY)
          }
        } else {
          // Regular user - use their assigned organization
          const org = data?.organizations || null
          setOrganization(org)
          // Cache organization
          try {
            if (org) {
              localStorage.setItem(ORG_CACHE_KEY, JSON.stringify(org))
            } else {
              localStorage.removeItem(ORG_CACHE_KEY)
            }
          } catch (e) {
            console.error('Error caching organization:', e)
          }
        }
      } catch (err) {
        console.error('Error fetching organization:', err)
        setError(err)
        setOrganization(null)
        localStorage.removeItem(ORG_CACHE_KEY)
      } finally {
        setLoading(false)
      }
    }

    fetchOrganization()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Function for super_admin to select an organization
  const selectOrganization = (org) => {
    localStorage.setItem(SELECTED_ORG_KEY, org.id)
    setOrganization(org)
    // Cache the selected organization
    try {
      localStorage.setItem(ORG_CACHE_KEY, JSON.stringify(org))
    } catch (e) {
      console.error('Error caching organization:', e)
    }
  }

  // Function to clear organization selection (for logout or switching)
  const clearOrganization = () => {
    localStorage.removeItem(SELECTED_ORG_KEY)
    localStorage.removeItem(ORG_CACHE_KEY)
    localStorage.removeItem(USER_ID_CACHE_KEY)
    setOrganization(null)
    setUserId(null)
  }

  const isSuperAdmin = userRole === 'super_admin'

  return {
    organization,
    userRole,
    userId,
    isSuperAdmin,
    loading,
    error,
    selectOrganization,
    clearOrganization,
    // Helper functions
    getFeature: (featureName) => {
      return organization?.settings?.features?.[featureName] || false
    },
    getBranding: (key) => {
      return organization?.settings?.branding?.[key] || null
    },
    getSetting: (path) => {
      // Support dot notation like 'features.daily_activity_log'
      const keys = path.split('.')
      let value = organization?.settings
      for (const key of keys) {
        value = value?.[key]
        if (value === undefined) return null
      }
      return value
    }
  }
}
